<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
    @click.self="close"
  >
    <div class="bg-card rounded-lg border shadow-lg w-full max-w-md mx-4">
      <div class="p-6">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-semibold">Close Position</h3>
          <button
            @click="close"
            class="text-muted-foreground hover:text-foreground"
          >
            <svg
              class="h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div v-if="position" class="mb-4 p-3 bg-muted/50 rounded-md">
          <p class="text-sm text-muted-foreground mb-1">Position Details</p>
          <p class="font-medium">{{ position.symbol }}</p>
          <p class="text-sm">
            {{ position.stock_qty.toLocaleString() }} shares @
            ${{ position.purchase_price.toFixed(2) }}
          </p>
        </div>

        <form @submit.prevent="handleSubmit" class="space-y-4">
          <div class="space-y-2">
            <label for="close_type" class="text-sm font-medium">Close Type</label>
            <div class="flex gap-4">
              <label class="flex items-center space-x-2 cursor-pointer">
                <input
                  id="close_type_expired"
                  v-model="closeData.close_type"
                  type="radio"
                  value="expired"
                  class="w-4 h-4 text-primary"
                  required
                />
                <span class="text-sm">Expired</span>
              </label>
              <label class="flex items-center space-x-2 cursor-pointer">
                <input
                  id="close_type_closed"
                  v-model="closeData.close_type"
                  type="radio"
                  value="closed"
                  class="w-4 h-4 text-primary"
                  required
                />
                <span class="text-sm">Closed Manually</span>
              </label>
            </div>
          </div>

          <div class="space-y-2">
            <label for="closing_price" class="text-sm font-medium">Closing Price</label>
            <input
              id="closing_price"
              v-model.number="closeData.closing_price"
              type="number"
              step="0.01"
              min="0.01"
              placeholder="195.50"
              class="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              required
              @input="calculateRealizedPnL"
            />
          </div>

          <div class="space-y-2">
            <label for="closing_date" class="text-sm font-medium">Closing Date</label>
            <DatePicker
              id="closing_date"
              v-model="closeData.closing_date"
              placeholder="Select closing date..."
              required
            />
          </div>

          <div class="space-y-2">
            <label for="closing_notes" class="text-sm font-medium">Closing Notes (Optional)</label>
            <textarea
              id="closing_notes"
              v-model="closeData.closing_notes"
              rows="3"
              placeholder="Add any notes about closing this position..."
              class="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>

          <!-- Realized P&L Preview -->
          <div
            v-if="closeData.closing_price && position"
            class="p-3 rounded-md"
            :class="
              realizedPnL >= 0
                ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
            "
          >
            <p class="text-sm font-medium mb-1">Realized P&L Preview</p>
            <p
              class="text-lg font-bold"
              :class="realizedPnL >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'"
            >
              {{ realizedPnL >= 0 ? '+' : ''
              }}${{
                realizedPnL.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
              }}
            </p>
          </div>

          <div class="flex gap-3 pt-4">
            <button
              type="button"
              @click="close"
              class="flex-1 inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4"
            >
              Cancel
            </button>
            <button
              type="submit"
              :disabled="isClosing || !closeData.close_type || !closeData.closing_price || !closeData.closing_date"
              class="flex-1 inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4"
            >
              <svg
                v-if="isClosing"
                class="mr-2 h-4 w-4 animate-spin"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              {{ isClosing ? 'Closing...' : 'Close Position' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { apiClient, type Position, type PositionClose } from '@/lib/api'
import DatePicker from '@/components/DatePicker.vue'

interface Props {
  isOpen: boolean
  position: Position | null
}

const props = defineProps<Props>()

const emit = defineEmits<{
  close: []
  closed: []
}>()

const isClosing = ref(false)

const closeData = ref<PositionClose>({
  close_type: 'closed',
  closing_price: 0,
  closing_date: '',
  closing_notes: null,
})

// Calculate realized P&L preview
const realizedPnL = computed(() => {
  if (!props.position || !closeData.value.closing_price) return 0
  // Formula: (closing_price * stock_qty) - cash + premium
  return (
    closeData.value.closing_price * props.position.stock_qty -
    props.position.cash +
    props.position.premium
  )
})

// Watch for position changes to pre-fill current price
watch(
  () => props.position,
  (newPosition) => {
    if (newPosition && newPosition.current_price) {
      closeData.value.closing_price = newPosition.current_price
    }
    // Reset form when position changes
    if (newPosition) {
      closeData.value.closing_date = new Date().toISOString().split('T')[0]
    }
  },
  { immediate: true }
)

// Watch for close type to auto-fill price
watch(
  () => closeData.value.close_type,
  (newType) => {
    if (newType === 'expired') {
      // If position has strike price, use it as closing price (as requested)
      // Otherwise default to 0 for expired options
      if (props.position?.strike_price) {
        closeData.value.closing_price = props.position.strike_price
      } else {
        closeData.value.closing_price = 0
      }
    } else if (newType === 'closed' && props.position?.current_price) {
      // Revert to current price if switching back to manual close
      closeData.value.closing_price = props.position.current_price
    }
  }
)

const calculateRealizedPnL = () => {
  // This is handled by the computed property
}

const handleSubmit = async () => {
  if (!props.position) return

  isClosing.value = true
  try {
    await apiClient.closePosition(props.position.id, closeData.value)
    emit('closed')
    close()
  } catch (error) {
    console.error('Failed to close position:', error)
    alert('Failed to close position. Please try again.')
  } finally {
    isClosing.value = false
  }
}

const close = () => {
  // Reset form
  closeData.value = {
    close_type: 'closed',
    closing_price: 0,
    closing_date: '',
    closing_notes: null,
  }
  emit('close')
}
</script>

